import { CircuitBenefitType } from '@/types/circuits/CircuitBenefitType';
import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import { CircuitFormData } from '@/types/circuits/CircuitFormData';
import { createCircuitDay } from '@/utils/circuits/createEmptyCircuitDays';
import { parseCircuitBenefitType } from '@/utils/circuits/parseCircuitBenefitType';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';

export const useCircuitEditorialModal = ({
  index,
  days,
}: {
  index: number;
  days: CircuitFormData['circuitDays'];
}) => {
  const { setValue } = useFormContext<CircuitFormData>();

  const steps = useMemo(() => days[index]?.steps ?? [], [days, index]);

  const getLastTransportStepIndexForDay = useCallback(
    (dayIndex: number) => {
      const daySteps = days[dayIndex]?.steps ?? [];
      const lastStepWithTransport = daySteps.findLast(
        (step) =>
          step.transport.standardBenefit ||
          step.transport.optionalBenefits.length > 0,
      );
      return lastStepWithTransport
        ? daySteps.indexOf(lastStepWithTransport)
        : -1;
    },
    [days],
  );

  const getLastAccommodationStepIndexForDay = useCallback(
    (dayIndex: number) => {
      const daySteps = days[dayIndex]?.steps ?? [];
      const lastStepWithAccommodation = daySteps.findLast(
        (step) =>
          step.accommodations?.standardBenefit ||
          step.accommodations?.optionalBenefits.length > 0,
      );
      return lastStepWithAccommodation
        ? daySteps.indexOf(lastStepWithAccommodation)
        : -1;
    },
    [days],
  );

  const addMultiDayBenefits = useCallback(
    ({
      standardBenefit,
      optionalBenefits,
      standardBenefitPensionType,
      optionalBenefitsPensionTypes,
      stepIndex,
      stepId,
      daysToLoopOver,
      startIndex,
      circuitBenefitType,
      nbDays,
      indexFirstDay,
    }: {
      standardBenefit: BenefitFormData;
      optionalBenefits: BenefitFormData[];
      standardBenefitPensionType?: HotelPensionType;
      optionalBenefitsPensionTypes?: Record<string, HotelPensionType>;
      stepIndex: number;
      stepId: string;
      daysToLoopOver: number;
      startIndex: number;
      nbDays: number;
      circuitBenefitType: 'transport' | 'accommodations';
      indexFirstDay: number;
    }) => {
      for (let i = 1; i <= daysToLoopOver; i++) {
        const dayIndex = startIndex + i;
        const dayStepIndex =
          circuitBenefitType === 'transport'
            ? getLastTransportStepIndexForDay(dayIndex)
            : getLastAccommodationStepIndexForDay(dayIndex);
        if (days[dayIndex].steps.length === dayStepIndex + 1)
          setValue(
            `circuitDays.${dayIndex}.steps.${dayStepIndex + 1}`,
            createCircuitDay().steps[0],
          );
        try {
          setValue(
            `circuitDays.${dayIndex}.steps.${dayStepIndex + 1}.${circuitBenefitType}.id`,
            stepId,
          );
          setValue(
            `circuitDays.${dayIndex}.steps.${dayStepIndex + 1}.${circuitBenefitType}.standardBenefit`,
            standardBenefit,
          );
          setValue(
            `circuitDays.${dayIndex}.steps.${dayStepIndex + 1}.${circuitBenefitType}.optionalBenefits`,
            optionalBenefits,
          );
          setValue(
            `circuitDays.${dayIndex}.steps.${dayStepIndex + 1}.${circuitBenefitType}.standardBenefitPensionType`,
            standardBenefitPensionType,
          );
          setValue(
            `circuitDays.${dayIndex}.steps.${dayStepIndex + 1}.${circuitBenefitType}.optionalBenefitsPensionTypes`,
            optionalBenefitsPensionTypes ?? {},
          );
          // To make sure steps[stepIndex] is up to date with the new values
          setTimeout(() => {
            setValue(
              `circuitDays.${dayIndex}.steps.${dayStepIndex + 1}.${circuitBenefitType}.standardDisplays`,
              steps[stepIndex][circuitBenefitType].standardDisplays,
            );
            setValue(
              `circuitDays.${dayIndex}.steps.${dayStepIndex + 1}.${circuitBenefitType}.optionalDisplays`,
              steps[stepIndex][circuitBenefitType].optionalDisplays,
            );
            setValue(
              `circuitDays.${dayIndex}.steps.${dayStepIndex + 1}.${circuitBenefitType}.time`,
              steps[stepIndex][circuitBenefitType].time,
            );
          }, 0);
          setValue(
            `circuitDays.${dayIndex}.steps.${dayStepIndex + 1}.${circuitBenefitType}.nbDays`,
            Math.max(nbDays, 1),
          );
          setValue(
            `circuitDays.${dayIndex}.steps.${dayStepIndex + 1}.${circuitBenefitType}.indexFirstDay`,
            indexFirstDay,
          );
        } catch (error) {
          // reset
          setValue(
            `circuitDays.${dayIndex}.steps.${dayStepIndex + 1}.${circuitBenefitType}`,
            createCircuitDay().steps[0][circuitBenefitType],
          );
          console.error(error);
        }
      }
    },
    [
      days,
      getLastAccommodationStepIndexForDay,
      getLastTransportStepIndexForDay,
      setValue,
      steps,
    ],
  );

  const addBenefits = useCallback(
    ({
      standardBenefit,
      optionalBenefits,
      standardBenefitPensionType,
      optionalBenefitsPensionTypes,
      type,
      stepIndex,
      stepId,
      isModification = false,
      stepNbDays = 1,
    }: {
      standardBenefit: BenefitFormData;
      optionalBenefits: BenefitFormData[];
      standardBenefitPensionType?: HotelPensionType;
      optionalBenefitsPensionTypes?: Record<string, HotelPensionType>;
      type: CircuitBenefitType;
      stepIndex: number;
      stepId: string;
      isModification?: boolean;
      stepNbDays?: number;
    }) => {
      const circuitBenefitType = parseCircuitBenefitType(type);
      setValue(
        `circuitDays.${index}.steps.${stepIndex}.${circuitBenefitType}.standardBenefit`,
        standardBenefit,
      );
      setValue(
        `circuitDays.${index}.steps.${stepIndex}.${circuitBenefitType}.optionalBenefits`,
        optionalBenefits,
      );
      setValue(
        `circuitDays.${index}.steps.${stepIndex}.${circuitBenefitType}.standardBenefitPensionType`,
        standardBenefitPensionType,
      );
      setValue(
        `circuitDays.${index}.steps.${stepIndex}.${circuitBenefitType}.optionalBenefitsPensionTypes`,
        optionalBenefitsPensionTypes ?? {},
      );
      setValue(
        `circuitDays.${index}.steps.${stepIndex}.${circuitBenefitType}.indexFirstDay`,
        index,
      );
      if (
        circuitBenefitType === 'transport' ||
        circuitBenefitType === 'accommodations'
      ) {
        if (!isModification)
          addMultiDayBenefits({
            standardBenefit,
            optionalBenefits,
            standardBenefitPensionType,
            optionalBenefitsPensionTypes,
            circuitBenefitType,
            stepIndex,
            stepId,
            startIndex: index,
            daysToLoopOver: stepNbDays - 1,
            nbDays: stepNbDays,
            indexFirstDay: index,
          });
        else {
          const currentStepsWithId = days
            .flatMap((day) => day.steps.map((step) => step[circuitBenefitType]))
            .filter((step) => step && step.id === stepId);

          // Find the first occurrence to get indexFirstDay and original nbDays
          const firstStep = currentStepsWithId[0];
          const indexFirstDay = firstStep?.indexFirstDay ?? index;
          const originalNbDays = currentStepsWithId.length;
          const totalNbDays = Math.min(stepNbDays, days.length - indexFirstDay);

          // Update all days in the range
          days.forEach((day, dayIndex) => {
            day.steps.forEach((dayStep, dayStepIndex) => {
              if (dayStep[circuitBenefitType]?.id === stepId) {
                const isInRange =
                  dayIndex >= indexFirstDay &&
                  dayIndex < indexFirstDay + totalNbDays;
                if (isInRange) {
                  setValue(
                    `circuitDays.${dayIndex}.steps.${dayStepIndex}.${circuitBenefitType}.standardBenefit`,
                    standardBenefit,
                  );
                  setValue(
                    `circuitDays.${dayIndex}.steps.${dayStepIndex}.${circuitBenefitType}.optionalBenefits`,
                    optionalBenefits,
                  );
                  setValue(
                    `circuitDays.${dayIndex}.steps.${dayStepIndex}.${circuitBenefitType}.standardBenefitPensionType`,
                    standardBenefitPensionType,
                  );
                  setValue(
                    `circuitDays.${dayIndex}.steps.${dayStepIndex}.${circuitBenefitType}.optionalBenefitsPensionTypes`,
                    optionalBenefitsPensionTypes ?? {},
                  );
                  setValue(
                    `circuitDays.${dayIndex}.steps.${dayStepIndex}.${circuitBenefitType}.standardDisplays`,
                    steps[stepIndex][circuitBenefitType].standardDisplays,
                  );
                  setValue(
                    `circuitDays.${dayIndex}.steps.${dayStepIndex}.${circuitBenefitType}.optionalDisplays`,
                    steps[stepIndex][circuitBenefitType].optionalDisplays,
                  );
                  setValue(
                    `circuitDays.${dayIndex}.steps.${dayStepIndex}.${circuitBenefitType}.time`,
                    steps[stepIndex][circuitBenefitType].time,
                  );
                  setValue(
                    `circuitDays.${dayIndex}.steps.${dayStepIndex}.${circuitBenefitType}.nbDays`,
                    totalNbDays,
                  );
                  setValue(
                    `circuitDays.${dayIndex}.steps.${dayStepIndex}.${circuitBenefitType}.indexFirstDay`,
                    indexFirstDay,
                  );
                } else {
                  setValue(
                    `circuitDays.${dayIndex}.steps.${dayStepIndex}.${circuitBenefitType}.optionalBenefits`,
                    [],
                  );
                  setValue(
                    `circuitDays.${dayIndex}.steps.${dayStepIndex}.${circuitBenefitType}`,
                    createCircuitDay().steps[0][circuitBenefitType],
                  );
                }
              }
            });
          });

          // Add benefits to any new days needed
          if (totalNbDays > originalNbDays) {
            addMultiDayBenefits({
              standardBenefit,
              optionalBenefits,
              standardBenefitPensionType,
              optionalBenefitsPensionTypes,
              startIndex: indexFirstDay + originalNbDays - 1,
              daysToLoopOver: totalNbDays - originalNbDays,
              stepIndex,
              stepId,
              circuitBenefitType,
              nbDays: totalNbDays,
              indexFirstDay,
            });
          }
        }
      }
    },
    [addMultiDayBenefits, days, index, setValue, steps],
  );

  const lastTransportStepIndex = useMemo(() => {
    const lastStepWithTransport = steps.findLast(
      (step) =>
        step.transport?.standardBenefit ||
        step.transport?.optionalBenefits?.length > 0,
    );
    return lastStepWithTransport ? steps.indexOf(lastStepWithTransport) : -1;
  }, [steps]);

  const lastMealStepIndex = useMemo(() => {
    const lastStepWithMeal = steps.findLast(
      (step) =>
        step?.meals?.standardBenefit ||
        step?.meals?.optionalBenefits?.length > 0,
    );
    return lastStepWithMeal ? steps.indexOf(lastStepWithMeal) : -1;
  }, [steps]);

  const lastAccommodationStepIndex = useMemo(() => {
    const lastStepWithAccommodation = steps.findLast(
      (step) =>
        step?.accommodations?.standardBenefit ||
        step?.accommodations?.optionalBenefits?.length > 0,
    );
    return lastStepWithAccommodation
      ? steps.indexOf(lastStepWithAccommodation)
      : -1;
  }, [steps]);

  const lastActivityStepIndex = useMemo(() => {
    const lastStepWithActivity = steps.findLast(
      (step) =>
        step?.activities?.standardBenefit ||
        step?.activities?.optionalBenefits?.length > 0,
    );
    return lastStepWithActivity ? steps.indexOf(lastStepWithActivity) : -1;
  }, [steps]);

  const maxBenefitStepIndex = useMemo(
    () =>
      Math.max(
        lastTransportStepIndex,
        lastMealStepIndex,
        lastAccommodationStepIndex,
        lastActivityStepIndex,
      ),
    [
      lastTransportStepIndex,
      lastMealStepIndex,
      lastAccommodationStepIndex,
      lastActivityStepIndex,
    ],
  );

  return {
    addBenefits,
    lastTransportStepIndex,
    lastMealStepIndex,
    lastAccommodationStepIndex,
    lastActivityStepIndex,
    maxBenefitStepIndex,
    steps,
  };
};
